/**
 * One shared Web Audio context for the whole app.
 *
 * iOS creates a context in the "suspended" state unless it is created or
 * resumed during a user gesture, and audio routed through a suspended context
 * is silent. Call unlockAudio() synchronously from a tap handler.
 */
let ctx: AudioContext | undefined;
let primed = false;

/**
 * Returns the shared context, or undefined when the environment has no Web
 * Audio (very old browsers, jsdom). Callers then fall back to their default.
 */
export function getAudioContext(): AudioContext | undefined {
    if (!ctx) {
        const Ctor = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) {
            return undefined;
        }
        ctx = new Ctor();
    }
    return ctx;
}

/** Resumes the shared context. Must be called synchronously inside a tap/click handler. */
export function unlockAudio(): void {
    try {
        const c = getAudioContext();
        if (!c) {
            return;
        }
        if (c.state !== 'running') {
            c.resume().catch(() => undefined);
        }
        if (!primed) {
            // iOS quirk: a silent buffer started inside the gesture fully unlocks output.
            const src = c.createBufferSource();
            src.buffer = c.createBuffer(1, 1, 22050);
            src.connect(c.destination);
            src.start(0);
            primed = true;
        }
    } catch {
        // Audio unavailable; the game's "tap to hear" fallback covers this.
    }
}
